import { type PropsWithChildren, type ReactElement, useCallback, useRef, useState } from 'react';
import { createContext, useContext, useEffect } from 'react';
import { useGeolocated } from 'react-geolocated';

export type GeolocationProviderCtx = {
  getGeolocation: (() => void) | undefined;
  getGeolocationAsync: (() => Promise<GeolocationCoordinates>) | undefined;
  error?: GeolocationPositionError;
  coords?: GeolocationCoordinates;
  isGeolocationEnabled: boolean;
  isGeolocationAvailable: boolean;
  loading: boolean;
};

const GeolocationCtx = createContext<GeolocationProviderCtx>({
  isGeolocationAvailable: false,
  isGeolocationEnabled: false,
  loading: false,
  getGeolocation: undefined,
  getGeolocationAsync: undefined,
});

const GeolocationProvider = ({ children }: PropsWithChildren): ReactElement => {
  const [locationRequested, setLocationRequested] = useState<boolean>(false);
  const { coords, isGeolocationAvailable, isGeolocationEnabled, positionError, getPosition } =
    useGeolocated({
      positionOptions: {
        enableHighAccuracy: true,
        // If the user refreshes the page and clicks `My location` again it will use the last GPS coordinate
        // if it's less than 60s old
        maximumAge: 60000,
        // How long to try acquiring the users location, this includes the time it takes the users to click the
        // Allow or Block buttons on the permission dialog
        timeout: 30000,
      },
      userDecisionTimeout: 10000, // Amount of time to wait after asking the user if it's ok to use location services
      watchPosition: true,
      suppressLocationOnMount: true,
    });

  type GeolocationPromise = {
    resolve: (value: GeolocationCoordinates) => void;
    reject: (reason?: GeolocationPositionError | Error) => void;
  };

  const pendingPromises = useRef<GeolocationPromise[]>([]);

  useEffect(() => {
    if (coords) {
      pendingPromises.current.forEach(({ resolve }) => resolve(coords));
      pendingPromises.current = [];
    } else if (positionError) {
      pendingPromises.current.forEach(({ reject }) => reject(positionError));
      pendingPromises.current = [];
    }
  }, [coords, positionError]);

  const loading = locationRequested && !coords && !positionError;

  const getGeolocation = useCallback(() => {
    // If we have `coords` we're already watching the users position
    if (!coords) {
      setLocationRequested(true);
      getPosition();
    }
  }, [coords, getPosition]);

  const getGeolocationAsync = useCallback((): Promise<GeolocationCoordinates> => {
    return new Promise((resolve, reject) => {
      if (coords) {
        return resolve(coords);
      }

      if (positionError) {
        return reject(positionError);
      }

      // Set up timeout to clear the promise
      const timeoutId = setTimeout(() => {
        // Remove this promise from pending promises
        pendingPromises.current = pendingPromises.current.filter((p) => p.resolve !== resolve);
        setLocationRequested(false);
        reject(new Error('Geolocation request timed out'));
      }, 30000);

      // Add this promise to pending promises
      pendingPromises.current.push({
        resolve: (coords) => {
          clearTimeout(timeoutId);
          resolve(coords);
        },
        reject: (error) => {
          clearTimeout(timeoutId);
          reject(error);
        },
      });

      // Trigger position request
      setLocationRequested(true);
      getPosition();
    });
  }, [coords, positionError, getPosition]);

  return (
    <GeolocationCtx.Provider
      value={{
        loading,
        error: positionError,
        coords,
        isGeolocationEnabled,
        isGeolocationAvailable,
        getGeolocation,
        getGeolocationAsync,
      }}>
      {children}
    </GeolocationCtx.Provider>
  );
};

export const useGeolocation = (): GeolocationProviderCtx =>
  useContext<GeolocationProviderCtx>(GeolocationCtx);

export default GeolocationProvider;
